import { useEffect, useMemo, useRef, useState } from 'react';
import { Reimbursement } from '../../../interfaces/reimbursment';

const SELECTION_SCOPE_IGNORED_PARAMS = new Set(['page', 'pageSize', 'sort']);

const getSelectionScopeKey = (searchParams?: URLSearchParams) => {
  if (!searchParams) return '';

  const scopeParams = new URLSearchParams();
  searchParams.forEach((value, key) => {
    if (!SELECTION_SCOPE_IGNORED_PARAMS.has(key)) {
      scopeParams.append(key, value);
    }
  });

  return scopeParams.toString();
};

const useReimbursementSelection = (
  reimbursements: Reimbursement[] = [],
  searchParams?: URLSearchParams
) => {
  const [selectedReimbursementsById, setSelectedReimbursementsById] = useState<
    Map<number, Reimbursement>
  >(new Map());

  const selectionScopeKey = useMemo(
    () => getSelectionScopeKey(searchParams),
    [searchParams]
  );
  const previousSelectionScopeKey = useRef(selectionScopeKey);

  useEffect(() => {
    if (previousSelectionScopeKey.current !== selectionScopeKey) {
      setSelectedReimbursementsById(new Map());
      previousSelectionScopeKey.current = selectionScopeKey;
    }
  }, [selectionScopeKey]);

  useEffect(() => {
    setSelectedReimbursementsById((previousSelection) => {
      let hasUpdates = false;
      const nextSelection = new Map(previousSelection);

      reimbursements.forEach((reimbursement) => {
        if (nextSelection.has(reimbursement.id)) {
          nextSelection.set(reimbursement.id, reimbursement);
          hasUpdates = true;
        }
      });

      return hasUpdates ? nextSelection : previousSelection;
    });
  }, [reimbursements]);

  const selectedReimbursementIds = useMemo(
    () => new Set(selectedReimbursementsById.keys()),
    [selectedReimbursementsById]
  );

  const selectedReimbursements = useMemo(
    () => Array.from(selectedReimbursementsById.values()),
    [selectedReimbursementsById]
  );

  const allCurrentPageSelected =
    reimbursements.length > 0 &&
    reimbursements.every((reimbursement) =>
      selectedReimbursementIds.has(reimbursement.id)
    );

  const toggleReimbursement = (
    reimbursement: Reimbursement,
    selected: boolean
  ) => {
    setSelectedReimbursementsById((previousSelection) => {
      const nextSelection = new Map(previousSelection);

      if (selected) {
        nextSelection.set(reimbursement.id, reimbursement);
      } else {
        nextSelection.delete(reimbursement.id);
      }

      return nextSelection;
    });
  };

  const toggleCurrentPage = (selected: boolean) => {
    setSelectedReimbursementsById((previousSelection) => {
      const nextSelection = new Map(previousSelection);

      reimbursements.forEach((reimbursement) => {
        if (selected) {
          nextSelection.set(reimbursement.id, reimbursement);
        } else {
          nextSelection.delete(reimbursement.id);
        }
      });

      return nextSelection;
    });
  };

  return {
    allCurrentPageSelected,
    selectedReimbursementIds,
    selectedReimbursements,
    toggleCurrentPage,
    toggleReimbursement,
  };
};

export default useReimbursementSelection;
